import React, { useState, useEffect, useMemo } from 'react';
import { Circle, AlertCircle, CheckCircle2 } from 'lucide-react';
import { StepConfig } from './DispositionWorkflowBuilder';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

interface WorkflowStepsPreviewProps {
  steps: StepConfig[];
  workflowName: string;
}

interface StepState {
  checked: boolean;
  note: string;
  isComplete: boolean;
}

const WorkflowStepsPreview: React.FC<WorkflowStepsPreviewProps> = ({ steps, workflowName }) => {
  // Track state for each step
  const [stepStates, setStepStates] = useState<StepState[]>([]);

  // Initialize or update step states when steps change
  useEffect(() => {
    // Preserve existing state when possible
    const newStepStates = steps.map((_, index) => {
      const existingState = stepStates[index] || { checked: false, note: '', isComplete: false };
      return existingState;
    });
    setStepStates(newStepStates);
  }, [JSON.stringify(steps), JSON.stringify(stepStates)]);

  // Check if a step is enabled (first step is always enabled, others require previous step to be complete)
  const isStepEnabled = (index: number): boolean => {
    if (index === 0) return true;
    return stepStates[index - 1]?.isComplete || false;
  };

  // Check if all steps are complete
  const areAllStepsComplete = () => {
    if (steps.length === 0) return false;
    return steps.every((_, index) => stepStates[index]?.isComplete || false);
  };

  // Handle checkbox change
  const handleCheckboxChange = (index: number, checked: boolean) => {
    if (!isStepEnabled(index)) return;

    const newStepStates = [...stepStates];
    newStepStates[index] = {
      ...newStepStates[index],
      checked,
      isComplete: checked || (steps[index].hasNote && !!newStepStates[index].note),
    };
    setStepStates(newStepStates);
  };

  // Handle note change
  const handleNoteChange = (index: number, note: string) => {
    if (!isStepEnabled(index)) return;

    const newStepStates = [...stepStates];
    newStepStates[index] = {
      ...newStepStates[index],
      note,
      isComplete: !!note || (steps[index].hasCheckbox && newStepStates[index].checked),
    };
    setStepStates(newStepStates);
  };

  return (
    <div className="flex h-full flex-col rounded-md border border-gray-800 bg-[#161616] p-4">
      <h3 className="mb-4 text-sm font-medium uppercase text-gray-400">Interactive Preview</h3>

      <div className="rounded-md border border-gray-800 bg-[#111] p-4">
        <h4 className="mb-3 text-base font-medium text-white">
          {workflowName || 'Untitled Workflow'}
        </h4>

        {steps.length > 0 ? (
          <div className="space-y-6">
            {steps.map((step, index) => {
              const isEnabled = isStepEnabled(index);
              const stepState = stepStates[index] || {
                checked: false,
                note: '',
                isComplete: false,
              };

              return (
                <div
                  key={step.key}
                  className={`space-y-3 rounded-md border border-gray-800 p-3 transition-opacity ${isEnabled ? 'opacity-100' : 'opacity-50'}`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs text-white ${isEnabled ? (stepState.isComplete ? 'bg-[#D1F551] text-white' : 'bg-gray-800') : 'bg-gray-900'}`}
                    >
                      {stepState.isComplete ? (
                        <CheckCircle2 className="h-3 w-3 text-black" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <h5 className="text-sm font-medium text-white">
                      {step.title || `Step ${index + 1}`}
                    </h5>

                    {!isEnabled && (
                      <div className="ml-auto flex items-center text-xs text-gray-500">
                        <AlertCircle className="mr-1 h-3 w-3" />
                        Complete previous step
                      </div>
                    )}

                    {isEnabled && stepState.isComplete && (
                      <div className="ml-auto flex items-center text-xs text-[#D1F551]">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        Complete
                      </div>
                    )}
                  </div>

                  {step.instructions && (
                    <div className="ml-8 text-xs text-gray-400">{step.instructions}</div>
                  )}

                  <div className="ml-8 space-y-3">
                    {step.hasCheckbox && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`step-${index}-checkbox`}
                          checked={stepState.checked}
                          onCheckedChange={(checked) =>
                            handleCheckboxChange(index, checked === true)
                          }
                          disabled={!isEnabled}
                          className="border-gray-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                        />
                        <label
                          htmlFor={`step-${index}-checkbox`}
                          className={`text-sm ${isEnabled ? 'text-gray-300' : 'text-gray-500'}`}
                        >
                          Mark as complete
                        </label>
                      </div>
                    )}

                    {step.hasNote && (
                      <div className="space-y-1">
                        <label
                          htmlFor={`step-${index}-note`}
                          className={`block text-xs ${isEnabled ? 'text-gray-400' : 'text-gray-600'}`}
                        >
                          Notes
                        </label>
                        <Textarea
                          id={`step-${index}-note`}
                          value={stepState.note}
                          onChange={(e) => handleNoteChange(index, e.target.value)}
                          disabled={!isEnabled}
                          placeholder={
                            isEnabled ? 'Enter notes here...' : 'Complete previous step first'
                          }
                          className="h-20 resize-none border-gray-800 bg-[#161616] text-sm text-white placeholder:text-gray-600 focus:border-blue-600"
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Resolve Alert Button */}
            <div className="mt-8 flex justify-center">
              <Button
                type="button"
                disabled={!areAllStepsComplete()}
                className={`w-full ${areAllStepsComplete() ? '' : 'bg-gray-800 text-gray-500'}`}
              >
                Resolve Alert
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-6 text-center text-gray-500">
            <Circle className="mx-auto mb-2 h-8 w-8 opacity-50" />
            <p>No steps configured</p>
            <p className="mt-1 text-xs">Add steps to see preview</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowStepsPreview;
